import { useCallback } from 'react';

import type { ActorListEnum, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { FlushMutation } from '../types/props.type';
import type { SubmitHandler } from '../types/submit-handler.type';

interface Params {
  readonly folderBusinessEntityId: Id;
  readonly list: ActorListEnum;
  readonly directMutation?: FlushMutation;
  readonly handleFlushSuccess: () => void;
}

interface FormikArrayActions {
  readonly flushInForm: () => void;
}

export const useFlushFolderBusinessEntityActorsSubmit = ({
  folderBusinessEntityId,
  list,
  directMutation,
  handleFlushSuccess,
}: Readonly<Params>) => {
  const handleSubmitFactory = useCallback(
    ({ flushInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          flushInForm();

          handleFlushSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              actorableId: folderBusinessEntityId,
              list,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [folderBusinessEntityId, directMutation, handleFlushSuccess, list],
  );

  return { handleSubmitFactory };
};
